import { createTheme, alpha } from "@mui/material/styles";

// Premium Color Palette
const palette = {
  primary: {
    main: "#1a1a1a", // Rich Black
    light: "#404040",
    dark: "#000000",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#FF7300", // Metallic Gold
    light: "#FFA55C",
    dark: "#D15E00",
    contrastText: "#000000",
  },
  background: {
    default: "#F9F9F9", // Soft Off-White
    paper: "#FFFFFF", // Pure White
    subtle: "#F5F5F7",
  },
  text: {
    primary: "#1A1A1A",
    secondary: "#666666",
    disabled: "#9E9E9E",
  },
  success: {
    main: "#2E7D32",
    light: "#4CAF50",
  },
  error: {
    main: "#D32F2F",
    light: "#EF5350",
  },
  warning: {
    main: "#ED6C02",
    light: "#FF9800",
  },
  info: {
    main: "#0288D1",
    light: "#03A9F4",
  },
  divider: "rgba(0, 0, 0, 0.08)",
};

const theme = createTheme({
  palette,
  typography: {
    fontFamily: '"Montserrat", "Inter", "Roboto", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "3rem",
      lineHeight: 1.16,
      letterSpacing: "-0.015em",
      color: palette.primary.main,
    },
    h2: {
      fontWeight: 600,
      fontSize: "2.25rem",
      lineHeight: 1.2,
      letterSpacing: "-0.008em",
      color: palette.primary.main,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.3,
      letterSpacing: "0em",
      color: palette.primary.main,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.35,
      letterSpacing: "0.007em",
    },
    h5: {
      fontWeight: 500,
      fontSize: "1.25rem",
      lineHeight: 1.4,
      letterSpacing: "0em",
    },
    h6: {
      fontWeight: 500,
      fontSize: "1.125rem",
      lineHeight: 1.5,
      letterSpacing: "0.007em",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
      color: palette.text.primary,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
      color: palette.text.secondary,
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "0.02em",
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
      color: palette.text.secondary,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 2px 4px rgba(0,0,0,0.05)",
    "0px 4px 8px rgba(0,0,0,0.05)",
    "0px 8px 16px rgba(0,0,0,0.05)",
    "0px 12px 24px rgba(0,0,0,0.05)",
    "0px 16px 32px rgba(0,0,0,0.05)",
    // ... rest standard mui shadows placeholder
    ...Array(19).fill("none"),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 50, // Pill shape default
          padding: "10px 24px",
          textTransform: "none",
          fontWeight: 600,
          transition: "all 0.3s ease",
          boxShadow: "none",
          // "&:hover": {
          //   transform: "translateY(-2px)",
          //   boxShadow: `0 6px 20px ${alpha(palette.secondary.main, 0.4)}`, // Dynamic shadow
          // },
        },
        containedPrimary: {
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          "&:hover": {
            backgroundColor: palette.primary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: palette.secondary.main,
          color: palette.secondary.contrastText,
          "&:hover": {
            backgroundColor: palette.secondary.dark,
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)", // Very subtle base shadow
          border: "1px solid rgba(0,0,0,0.03)",
          transition: "all 0.3s ease-in-out",
          background: "rgba(255, 255, 255, 0.8)", // Slight transparency
          backdropFilter: "blur(12px)", // Glassmorphism
          // "&:hover": {
          //   boxShadow: "0 15px 35px rgba(0, 0, 0, 0.08)",
          //   transform: "translateY(-4px)",
          //   borderColor: alpha(palette.secondary.main, 0.3), // Dynamic border on hover
          // },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none", // Remove default gradient overlay in dark mode if any
        },
        elevation1: {
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(palette.background.paper, 0.8),
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${palette.divider}`,
          boxShadow: "none",
          color: palette.text.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.background.paper,
          borderLeft: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            backgroundColor: palette.background.paper,
            transition: "all 0.2s",
            "& fieldset": {
              borderColor: alpha(palette.text.primary, 0.15),
            },
            "&:hover fieldset": {
              borderColor: palette.secondary.main,
            },
            "&.Mui-focused fieldset": {
              borderColor: palette.secondary.main,
              borderWidth: "2px",
            },
            "&.Mui-focused": {
              backgroundColor: "#fff",
              boxShadow: `0 4px 12px ${alpha(palette.secondary.main, 0.2)}`, // Dynamic glow
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: "4px 8px",
          "&.Mui-selected": {
            backgroundColor: alpha(palette.primary.main, 0.08),
            color: palette.primary.main,
            "& .MuiListItemIcon-root": {
              color: palette.primary.main,
            },
            "&:hover": {
              backgroundColor: alpha(palette.primary.main, 0.12),
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: palette.text.secondary,
          minWidth: 40,
        },
      },
    },
  },
});

export default theme;
